const DEFAULT_REQUIRED_SAMPLES = 3;
const DEFAULT_MAX_ATTEMPTS = 5;
const DEFAULT_MAX_LATENCY_MS = 1000;
const DEFAULT_REQUEST_TIMEOUT_MS = 2000;

const positiveIntegerOrDefault = (value, fallback) => (
  Number.isInteger(value) && value > 0 ? value : fallback
);

const positiveNumberOrDefault = (value, fallback) => (
  Number.isFinite(value) && value > 0 ? value : fallback
);

const normalizeBasePath = basePath => {
  const value = typeof basePath === 'string' && basePath.trim()
    ? basePath.trim()
    : '/application';
  const withLeadingSlash = `/${value.replace(/^\/+/, '')}`;
  return withLeadingSlash.replace(/\/+$/, '');
};

const getRuntimeBasePath = () => {
  const env = typeof window !== 'undefined' ? window.__ENV : undefined;
  return env?.REACT_APP_BASE_PATH || '/application';
};

export const buildServerTimeUrl = (basePath = getRuntimeBasePath()) => (
  `${normalizeBasePath(basePath)}/server-time`
);

const getMonotonicNow = () => {
  const perf = typeof performance !== 'undefined' ? performance : undefined;
  return perf && typeof perf.now === 'function' ? perf.now() : null;
};

const createSampleFailure = category => {
  const error = new Error(category);
  error.clockSkewCategory = category;
  return error;
};

const getFailureCategory = error => {
  if (error?.clockSkewCategory) return error.clockSkewCategory;
  if (error?.name === 'TimeoutError' || error?.name === 'AbortError') return 'timeout';
  return 'network';
};

const fetchWithTimeout = async (url, requestOptions, timeoutMs, fetchImpl, signal) => {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const options = controller
    ? { ...requestOptions, signal: controller.signal }
    : { ...requestOptions, signal };
  let didTimeout = false;
  let timeoutId;
  const abortRequest = () => controller?.abort();

  if (signal?.aborted) {
    abortRequest();
  } else {
    signal?.addEventListener('abort', abortRequest, { once: true });
  }

  const timeoutPromise = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      didTimeout = true;
      abortRequest();
      reject(new Error('clock-skew-timeout'));
    }, timeoutMs);
  });

  try {
    return await Promise.race([
      (async () => {
        const response = await fetchImpl(url, options);
        if (!response?.ok) return { response };

        try {
          return {
            response,
            payload: await response.json(),
          };
        } catch {
          throw createSampleFailure('malformed-response');
        }
      })(),
      timeoutPromise,
    ]);
  } catch (error) {
    if (didTimeout) {
      const timeoutError = new Error('clock-skew-timeout');
      timeoutError.name = 'TimeoutError';
      throw timeoutError;
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortRequest);
  }
};

const collectSample = async ({
  url,
  requestTimeoutMs,
  fetchImpl,
  signal,
}) => {
  const localStartMs = Date.now();
  const monotonicStartMs = getMonotonicNow();
  const { response, payload } = await fetchWithTimeout(
    url,
    {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
    },
    requestTimeoutMs,
    fetchImpl,
    signal,
  );

  if (!response?.ok) throw createSampleFailure('response');

  const localEndMs = Date.now();
  const monotonicEndMs = getMonotonicNow();
  const latencyMs = Number.isFinite(monotonicStartMs) && Number.isFinite(monotonicEndMs)
    ? monotonicEndMs - monotonicStartMs
    : localEndMs - localStartMs;

  if (!Number.isFinite(latencyMs) || latencyMs < 0) {
    throw createSampleFailure('invalid-latency');
  }
  if (!Number.isSafeInteger(payload?.serverTimeMs) || payload.serverTimeMs < 0) {
    throw createSampleFailure('malformed-response');
  }

  return {
    latencyMs,
    offsetMs: payload.serverTimeMs - ((localStartMs + localEndMs) / 2),
  };
};

const median = values => {
  const sortedValues = [...values].sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedValues.length / 2);
  if (sortedValues.length % 2 === 1) return sortedValues[middleIndex];
  return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2;
};

const unavailableResult = (failureCategory, validSampleCount, attemptCount) => ({
  status: 'unavailable',
  offsetMs: null,
  validSampleCount,
  attemptCount,
  failureCategory,
});

export const sampleClockSkew = async (options = {}) => {
  const requiredSamples = positiveIntegerOrDefault(
    options.requiredSamples,
    DEFAULT_REQUIRED_SAMPLES,
  );
  const maxAttempts = positiveIntegerOrDefault(options.maxAttempts, DEFAULT_MAX_ATTEMPTS);
  const maxLatencyMs = positiveNumberOrDefault(options.maxLatencyMs, DEFAULT_MAX_LATENCY_MS);
  const requestTimeoutMs = positiveNumberOrDefault(
    options.requestTimeoutMs,
    DEFAULT_REQUEST_TIMEOUT_MS,
  );
  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : undefined);

  if (typeof fetchImpl !== 'function') {
    return unavailableResult('configuration', 0, 0);
  }

  const offsets = [];
  let attemptCount = 0;
  let failureCategory = 'insufficient-valid-samples';
  const url = buildServerTimeUrl(options.basePath);

  while (
    attemptCount < maxAttempts
    && offsets.length < requiredSamples
    && !options.signal?.aborted
  ) {
    attemptCount += 1;
    try {
      const sample = await collectSample({
        url,
        requestTimeoutMs,
        fetchImpl,
        signal: options.signal,
      });
      if (sample.latencyMs > maxLatencyMs) {
        failureCategory = 'high-latency';
      } else {
        offsets.push(sample.offsetMs);
      }
    } catch (error) {
      failureCategory = getFailureCategory(error);
    }
  }

  if (offsets.length < requiredSamples) {
    return unavailableResult(failureCategory, offsets.length, attemptCount);
  }

  return {
    status: 'available',
    offsetMs: median(offsets),
    validSampleCount: offsets.length,
    attemptCount,
  };
};

export default sampleClockSkew;
